import { MAIL_COLUMN_META, type MailColumnId } from './mailColumns';

/**
 * Largeurs des colonnes de la page détail. Elles sont CALCULÉES à partir de la
 * largeur mesurée du volet, jamais déclarées en dur : un volet Outlook va de
 * ~320 px (par défaut, à droite d'une fenêtre partagée) à toute la largeur de
 * l'écran, et un jeu de largeurs fixes ne peut convenir aux deux.
 *
 * Module à part de FolderDetail pour une raison précise : le choix des colonnes à
 * ABANDONNER quand le volet est trop étroit doit se faire avec exactement la même
 * arithmétique que celle qui produit ensuite les largeurs. Deux calculs séparés
 * finiraient par se contredire — on retirerait une colonne au nom d'un seuil que
 * le calcul final n'applique pas. Ici, `fitMiddleColumns` appelle
 * `layoutFlexColumns`, la fonction même dont la grille se sert.
 */

/**
 * Rembourrage horizontal que Fluent ajoute à CHAQUE cellule, en-tête comprise.
 *
 * Terme indispensable et non cosmétique : les largeurs passées à
 * `columnSizingOptions` sont des largeurs de **contenu**
 * (`box-sizing: content-box`, `padding: 0 8px` — mesuré : une colonne demandée à
 * 393 px est rendue sur 409). L'ignorer sous-estimait le besoin de **16 px par
 * colonne**, soit 64 px pour quatre : à 430 px de volet, les quatre colonnes
 * finissaient toutes à leur minimum et la grille débordait de 2 px alors que le
 * calcul la croyait à l'aise.
 *
 * Couplé à la densité `size="small"` de la grille : un autre échelon changerait ce
 * rembourrage, et cette constante avec.
 */
export const CELL_PADDING = 16;
/** Créneau fixe de la colonne d'actions (⋯ et roue crantée), collée à droite. */
export const ACTIONS_COL_WIDTH = 48;
/** Poids de « Nom / Objet » dans le partage proportionnel. */
export const NAME_COL_WEIGHT = 240;
/** Plancher absolu de « Nom / Objet » — en dessous, la grille défile. */
export const NAME_COL_MIN = 140;
/**
 * Largeur de contenu en dessous de laquelle « Nom / Objet » est jugée trop courte
 * pour lire l'objet d'un mail, et où l'on préfère sacrifier la dernière colonne.
 * 150 px de contenu, soit ~166 px rendus.
 *
 * Pourquoi un seuil DISTINCT du plancher : sans lui, la règle « pas de défilement
 * horizontal » se satisfait d'un nom rabattu à ses 140 px minimaux — ce qui est
 * exactement le symptôme qu'on cherche à supprimer.
 *
 * Pourquoi pas plus haut : à 150, un volet de 340 px (celui du défaut signalé)
 * perd **une** colonne, « Envoyé le », et garde « De » avec un nom à 166 px rendus.
 * Un seuil de 165 lui ferait perdre les deux, ce qui dépasse la demande — le
 * réglage se joue à cet endroit précis, pas ailleurs. À l'inverse, descendre sous
 * ~150 ramènerait le nom vers son plancher de 140, c'est-à-dire vers le symptôme.
 *
 * Seuils MESURÉS pour le jeu par défaut (« De » + « Envoyé le »), figés par
 * `columnLayout.test.ts` — et non ceux qu'on obtient de tête, les restes d'arrondi
 * rendant un pixel ou deux à « Nom / Objet » :
 *
 * | largeur de grille | colonnes conservées | « Nom / Objet » rendu |
 * |-------------------|---------------------|-----------------------|
 * | ≥ 451 px          | De + Envoyé le      | ≥ 166 px              |
 * | 339 → 450 px      | De                  | 166 → 234 px          |
 * | < 339 px          | aucune              | toute la place        |
 *
 * Le seuil haut (451 et non ~431 comme le donnerait le seul prorata) vient des
 * MINIMUMS : à 431 px, « De » et « Envoyé le » sont déjà bloquées à 90 px chacune,
 * si bien que le nom ne récupère plus rien du partage proportionnel et retombe sur
 * son plancher. C'est la raison d'être du test qui fige ces valeurs — de tête, on
 * se trompe de 20 px.
 */
export const NAME_COL_READABLE = 150;

const weightOf = (id: string): number =>
  id === 'name' ? NAME_COL_WEIGHT : MAIL_COLUMN_META[id as MailColumnId].defaultWidth;

/** Largeur de contenu minimale d'une colonne, ancres comprises. */
export const columnMinWidth = (id: string): number =>
  id === 'name' ? NAME_COL_MIN : MAIL_COLUMN_META[id as MailColumnId].minWidth;

/**
 * Budget de largeurs de CONTENU réellement distribuable entre « Nom / Objet » et
 * les `middleCount` colonnes du milieu : le volet, moins la colonne d'actions,
 * moins le rembourrage de chaque cellule (colonnes du milieu + nom + actions).
 */
export function contentBudget(gridWidth: number, middleCount: number): number {
  return Math.max(0, gridWidth - ACTIONS_COL_WIDTH - CELL_PADDING * (middleCount + 2));
}

/**
 * Répartit `budget` px de contenu entre « Nom / Objet » et les colonnes `middle`,
 * au prorata de leur largeur naturelle et sans jamais descendre sous leur minimum.
 * Le reste de l'arrondi va à « Nom / Objet », de sorte que le total vaille
 * EXACTEMENT `budget` et que la colonne d'actions reste plaquée au bord droit.
 *
 * Si les minimums dépassent à eux seuls `budget`, le total renvoyé dépasse aussi :
 * c'est le cas « ça ne rentre pas », et c'est `fitMiddleColumns` qui le tranche.
 */
export function layoutFlexColumns(
  budget: number,
  middle: readonly MailColumnId[],
): Record<string, number> {
  const flexIds: string[] = ['name', ...middle];
  const weightSum = flexIds.reduce((sum, id) => sum + weightOf(id), 0);
  const widths: Record<string, number> = {};
  let allocated = 0;
  for (const id of flexIds) {
    widths[id] = Math.max(columnMinWidth(id), Math.floor((weightOf(id) / weightSum) * budget));
    allocated += widths[id];
  }
  widths.name = Math.max(NAME_COL_MIN, (widths.name ?? NAME_COL_MIN) + (budget - allocated));
  return widths;
}

/**
 * Sous-ensemble des colonnes demandées que le volet peut réellement porter :
 * on retire la DERNIÈRE (la plus à droite) tant que la grille déborderait ou que
 * « Nom / Objet » n'atteindrait pas {@link NAME_COL_READABLE}.
 *
 * La dernière et non la moins utile : c'est l'ordre affiché qui fait la priorité,
 * et l'utilisateur le contrôle déjà (glisser un en-tête, ou le sélecteur de
 * colonnes). Une notion d'importance câblée dans le code lui retirerait ce choix.
 *
 * Ce retrait est un fait d'AFFICHAGE : il ne touche pas aux préférences
 * persistées. Élargir le volet fait revenir les colonnes telles quelles.
 */
export function fitMiddleColumns(
  gridWidth: number,
  middle: readonly MailColumnId[],
): MailColumnId[] {
  // Avant la première mesure, on garde le jeu demandé : sans largeur connue il n'y
  // a rien à décider, et masquer par défaut ferait clignoter les colonnes au
  // premier rendu.
  if (gridWidth <= 0) return [...middle];

  const kept = [...middle];
  while (kept.length > 0) {
    // Le budget dépend du NOMBRE de colonnes retenues (chacune coûte son
    // rembourrage) : il est donc recalculé à chaque tour, et non une fois pour
    // toutes — retirer une colonne rend 16 px en plus de sa largeur.
    const budget = contentBudget(gridWidth, kept.length);
    const widths = layoutFlexColumns(budget, kept);
    const total = Object.values(widths).reduce((sum, w) => sum + w, 0);
    if (total <= budget && (widths.name ?? 0) >= NAME_COL_READABLE) break;
    kept.pop();
  }
  return kept;
}
